import { useEffect, useState } from "react";
import api, { formatError, formatRupiah } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, ShoppingCart, Loader2 } from "lucide-react";

export default function Sales() {
  const [sales, setSales] = useState(null);
  const [items, setItems] = useState([]);
  const [sources, setSources] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ item_id: "", quantity: "", unit_price: "", source_id: "none", note: "" });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [monthFilter, setMonthFilter] = useState("all");

  const fetchAll = () => {
    Promise.all([api.get("/sales"), api.get("/items"), api.get("/money-sources")])
      .then(([s, i, m]) => { setSales(s.data); setItems(i.data); setSources(m.data); })
      .catch(() => setSales([]));
  };
  useEffect(() => { fetchAll(); }, []);

  const selectedItem = items.find((i) => i.id === form.item_id);
  const total = (parseInt(form.quantity) || 0) * (parseFloat(form.unit_price) || 0);

  const handleItemChange = (id) => {
    const item = items.find((i) => i.id === id);
    setForm({ ...form, item_id: id, unit_price: item ? String(item.sell_price) : "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/sales", {
        item_id: form.item_id,
        quantity: parseInt(form.quantity) || 0,
        unit_price: parseFloat(form.unit_price) || 0,
        source_id: form.source_id === "none" ? null : form.source_id,
        note: form.note,
      });
      toast.success("Penjualan berhasil dicatat, stok berkurang otomatis");
      setFormOpen(false);
      setForm({ item_id: "", quantity: "", unit_price: "", source_id: "none", note: "" });
      fetchAll();
    } catch (err) {
      toast.error(formatError(err, "Gagal mencatat penjualan"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/sales/${deleting.id}`);
      toast.success("Penjualan dihapus, stok dikembalikan");
      setDeleting(null);
      fetchAll();
    } catch (err) {
      toast.error(formatError(err, "Gagal menghapus penjualan"));
    }
  };

  if (sales === null) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="sales-loading">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const monthKey = (iso) => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };
  const months = [...new Set(sales.map((s) => monthKey(s.created_at)))].sort().reverse();
  const monthLabel = (m) =>
    new Date(parseInt(m.slice(0, 4)), parseInt(m.slice(5, 7)) - 1).toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  const filteredSales = monthFilter === "all" ? sales : sales.filter((s) => monthKey(s.created_at) === monthFilter);
  const summary = {
    total: filteredSales.reduce((a, s) => a + s.total, 0),
    count: filteredSales.length,
    profit: filteredSales.reduce((a, s) => a + (s.unit_price - (s.unit_cost ?? 0)) * s.quantity, 0),
  };

  return (
    <div className="space-y-6" data-testid="sales-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Penjualan</h1>
          <p className="text-sm text-slate-500 mt-1">Catat penjualan barang — stok berkurang dan uang masuk otomatis</p>
        </div>
        <Button onClick={() => setFormOpen(true)} data-testid="add-sale-button" className="bg-indigo-600 hover:bg-indigo-700 text-white transition-colors duration-200">
          <Plus className="w-4 h-4 mr-2" /> Tambah Penjualan
        </Button>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <Select value={monthFilter} onValueChange={setMonthFilter}>
          <SelectTrigger data-testid="sales-month-filter" className="w-full sm:w-56 bg-white focus:ring-indigo-500">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="month-option-all">Semua Bulan</SelectItem>
            {months.map((m) => (
              <SelectItem key={m} value={m} data-testid={`month-option-${m}`}>{monthLabel(m)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="grid grid-cols-3 gap-3 flex-1" data-testid="monthly-summary">
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{monthFilter === "all" ? "Total Penjualan" : `Penjualan ${monthLabel(monthFilter)}`}</p>
            <p className="text-base font-heading font-bold text-indigo-600 mt-0.5" data-testid="monthly-total-value">{formatRupiah(summary.total)}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Transaksi</p>
            <p className="text-base font-heading font-bold text-slate-900 mt-0.5" data-testid="monthly-count-value">{summary.count}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Laba</p>
            <p className={`text-base font-heading font-bold mt-0.5 ${summary.profit < 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid="monthly-profit-value">{formatRupiah(summary.profit)}</p>
          </div>
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm overflow-hidden" data-testid="sales-table-card">
        <CardContent className="p-0">
          <div className="overflow-auto">
            <Table data-testid="sales-table">
              <TableHeader>
                <TableRow className="bg-slate-50/50 border-b border-slate-200">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Tanggal</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Barang</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Jml</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Harga</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Total</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Masuk Ke</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSales.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-16" data-testid="sales-empty">
                      <ShoppingCart className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-400">{monthFilter === "all" ? "Belum ada penjualan. Klik Tambah Penjualan untuk mulai." : `Tidak ada penjualan di ${monthLabel(monthFilter)}.`}</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSales.map((s) => (
                    <TableRow key={s.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors duration-200" data-testid={`sale-row-${s.id}`}>
                      <TableCell className="p-4 text-sm text-slate-500">{new Date(s.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}</TableCell>
                      <TableCell className="p-4 text-sm font-medium text-slate-800">{s.item_name}</TableCell>
                      <TableCell className="p-4 text-sm text-slate-600 text-right">{s.quantity}</TableCell>
                      <TableCell className="p-4 text-sm text-slate-600 text-right">{formatRupiah(s.unit_price)}</TableCell>
                      <TableCell className="p-4 text-sm font-semibold text-emerald-600 text-right" data-testid={`sale-total-${s.id}`}>{formatRupiah(s.total)}</TableCell>
                      <TableCell className="p-4">
                        {s.source_name ? <Badge variant="secondary" className="bg-indigo-50 text-indigo-600 hover:bg-indigo-50">{s.source_name}</Badge> : <span className="text-sm text-slate-400">-</span>}
                      </TableCell>
                      <TableCell className="p-4 text-right">
                        <Button variant="ghost" size="icon" data-testid={`delete-sale-button-${s.id}`} onClick={() => setDeleting(s)} className="text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={formOpen} onOpenChange={(open) => { setFormOpen(open); if (!open) setForm({ item_id: "", quantity: "", unit_price: "", source_id: "none", note: "" }); }}>
        <DialogContent className="sm:max-w-md" data-testid="sale-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Tambah Penjualan</DialogTitle>
            <DialogDescription>Stok barang akan berkurang otomatis sesuai jumlah terjual.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="sale-form">
            <div className="space-y-2">
              <Label>Barang</Label>
              <Select value={form.item_id} onValueChange={handleItemChange} required>
                <SelectTrigger data-testid="sale-item-select" className="focus:ring-indigo-500"><SelectValue placeholder="Pilih barang" /></SelectTrigger>
                <SelectContent>
                  {items.map((i) => (
                    <SelectItem key={i.id} value={i.id} data-testid={`sale-item-option-${i.id}`}>{i.name} (stok: {i.stock})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedItem && (
                <p className="text-xs text-slate-400" data-testid="sale-stock-hint">Stok tersedia: {selectedItem.stock} — harga jual: {formatRupiah(selectedItem.sell_price)}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sale-qty">Jumlah</Label>
                <Input id="sale-qty" type="number" min="1" data-testid="sale-quantity-input" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} required className="focus-visible:ring-indigo-500" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sale-price">Harga Satuan (Rp)</Label>
                <Input id="sale-price" type="number" min="0" data-testid="sale-price-input" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} required className="focus-visible:ring-indigo-500" />
              </div>
            </div>
            {selectedItem && parseInt(form.quantity) > selectedItem.stock && (
              <p className="text-xs text-rose-600" data-testid="sale-insufficient-hint">Jumlah melebihi stok tersedia (sisa {selectedItem.stock})</p>
            )}
            <div className="space-y-2">
              <Label>Uang Masuk Ke</Label>
              <Select value={form.source_id} onValueChange={(v) => setForm({ ...form, source_id: v })}>
                <SelectTrigger data-testid="sale-source-select" className="focus:ring-indigo-500"><SelectValue placeholder="Pilih tempat uang (opsional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="sale-source-none">Tidak dicatat ke tempat uang</SelectItem>
                  {sources.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`sale-source-option-${s.id}`}>{s.name} ({formatRupiah(s.balance)})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sale-note">Catatan</Label>
              <Input id="sale-note" data-testid="sale-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Opsional" className="focus-visible:ring-indigo-500" />
            </div>
            <div className="rounded-lg bg-indigo-50 px-4 py-3 flex items-center justify-between" data-testid="sale-total-display">
              <span className="text-sm font-medium text-indigo-700">Total Penjualan</span>
              <span className="text-lg font-heading font-bold text-indigo-700">{formatRupiah(total)}</span>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" data-testid="sale-form-cancel" onClick={() => setFormOpen(false)}>Batal</Button>
              <Button type="submit" data-testid="sale-form-submit" disabled={saving || !form.item_id} className="bg-indigo-600 hover:bg-indigo-700 text-white transition-colors">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Penjualan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-sale-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Hapus Penjualan?</AlertDialogTitle>
            <AlertDialogDescription>
              Penjualan <span className="font-semibold">{deleting?.item_name} x{deleting?.quantity}</span> akan dihapus. Stok barang dan saldo tempat uang akan dikembalikan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-sale-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-sale-confirm" onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700 text-white transition-colors">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
